import { useState, useEffect, ChangeEvent } from "react";
import { ArrowUpRight, Loader2, AlertCircle, CheckCircle2, X, Landmark, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Label } from "@/components/ui";
import { companyApi } from "@/services/api";
import { formatCurrency } from "@/lib/utils";

// Formata valor para exibição com máscara de moeda (1.234,56)
function formatCurrencyInput(value: string): string {
  // Remove tudo que não é número
  const numbers = value.replace(/\D/g, "");

  if (!numbers) return "";

  // Converte para centavos e formata
  const cents = parseInt(numbers, 10);
  const reais = cents / 100;

  return reais.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Converte valor formatado para número em reais
function parseCurrencyInput(value: string): number {
  if (!value) return 0;
  // Remove pontos de milhar e troca vírgula por ponto
  const normalized = value.replace(/\./g, "").replace(",", ".");
  return parseFloat(normalized) || 0;
}

interface BankAccount {
  id: string;
  bankCode: string;
  bankName: string;
  agency: string;
  accountNumber: string;
  accountDigit?: string;
  accountType: string;
  holderName: string;
  holderDocument: string;
  isVerified: boolean;
}

interface WithdrawalInfo {
  availableBalance: number;
  availableBalanceFormatted: string;
  blockedBalance: number;
  blockedBalanceFormatted: string;
  totalPrincipal: number;
  totalPrincipalFormatted: string;
  totalYield: number;
  totalYieldFormatted: string;
  minWithdrawalAmount: number;
  minWithdrawalAmountFormatted: string;
}

type WithdrawalType = "ALL" | "PRINCIPAL_ONLY" | "YIELD_ONLY" | "CUSTOM";

interface WithdrawModalProps {
  open: boolean;
  onClose: () => void;
  balance: number;
  onSuccess?: () => void;
}

type Step = "form" | "confirm" | "success";

export function WithdrawModal({ open, onClose, onSuccess }: WithdrawModalProps) {
  const [step, setStep] = useState<Step>("form");
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [withdrawalType, setWithdrawalType] = useState<WithdrawalType>("ALL");
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [withdrawalInfo, setWithdrawalInfo] = useState<WithdrawalInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadData();
      setStep("form");
      setWithdrawalType("ALL");
      setCustomAmount("");
      setSelectedAccountId("");
      setError(null);
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    setLoadingInfo(true);
    try {
      const [accounts, info] = await Promise.all([
        companyApi.dashboard.listBankAccounts(),
        companyApi.dashboard.getWithdrawalInfo(),
      ]);
      setBankAccounts(accounts);
      setWithdrawalInfo(info);
      if (accounts.length > 0) {
        setSelectedAccountId(accounts[0].id);
      }
    } catch (err: any) {
      setError(err.message || "Erro ao carregar dados");
    } finally {
      setLoading(false);
      setLoadingInfo(false);
    }
  };

  // Calcula o valor total baseado no tipo de resgate
  const getWithdrawalAmount = (): number => {
    if (!withdrawalInfo) return 0;

    switch (withdrawalType) {
      case "ALL":
        return withdrawalInfo.availableBalance;
      case "PRINCIPAL_ONLY":
        return withdrawalInfo.totalPrincipal;
      case "YIELD_ONLY":
        return withdrawalInfo.totalYield;
      case "CUSTOM":
        return Math.round(parseCurrencyInput(customAmount) * 100);
      default:
        return 0;
    }
  };

  // Calcula a divisão entre principal e rendimento
  const getWithdrawalSplit = (): { principal: number; yield: number } => {
    if (!withdrawalInfo) return { principal: 0, yield: 0 };

    const amount = getWithdrawalAmount();

    switch (withdrawalType) {
      case "ALL":
        return {
          principal: withdrawalInfo.totalPrincipal,
          yield: withdrawalInfo.totalYield,
        };
      case "PRINCIPAL_ONLY":
        return {
          principal: Math.min(amount, withdrawalInfo.totalPrincipal),
          yield: 0,
        };
      case "YIELD_ONLY":
        return {
          principal: 0,
          yield: Math.min(amount, withdrawalInfo.totalYield),
        };
      case "CUSTOM":
        // Para valor customizado: primeiro usa rendimento, depois principal
        if (amount <= withdrawalInfo.totalYield) {
          return { principal: 0, yield: amount };
        } else {
          return {
            yield: withdrawalInfo.totalYield,
            principal: amount - withdrawalInfo.totalYield,
          };
        }
      default:
        return { principal: 0, yield: 0 };
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const amountInCents = getWithdrawalAmount();
      await companyApi.dashboard.createWithdrawalRequest({
        amount: amountInCents,
        bankAccountId: selectedAccountId,
        withdrawalType,
      });
      setStep("success");
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || "Erro ao solicitar resgate");
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = bankAccounts.find((a) => a.id === selectedAccountId);
  const withdrawalAmount = getWithdrawalAmount();
  const withdrawalSplit = getWithdrawalSplit();
  const minAmount = withdrawalInfo?.minWithdrawalAmount || 10000; // R$ 100,00

  // Validação do valor
  const isValidAmount = (() => {
    if (!withdrawalInfo) return false;
    if (withdrawalAmount < minAmount) return false;
    if (withdrawalAmount > withdrawalInfo.availableBalance) return false;

    // Validações específicas por tipo
    if (withdrawalType === "PRINCIPAL_ONLY" && withdrawalInfo.totalPrincipal < minAmount) {
      return false;
    }
    if (withdrawalType === "YIELD_ONLY" && withdrawalInfo.totalYield < minAmount) {
      return false;
    }

    return true;
  })();

  const handleCustomAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCurrencyInput(e.target.value);
    setCustomAmount(formatted);
  };

  const handleClose = () => {
    setStep("form");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Form Step */}
        {step === "form" && (
          <>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5" />
                <CardTitle>Solicitar Resgate</CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={handleClose}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Saldo Disponível */}
              {loadingInfo ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : withdrawalInfo ? (
                <>
                  <div className="bg-muted rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Saldo Disponível</p>
                    <p className="text-2xl font-bold">{withdrawalInfo.availableBalanceFormatted}</p>
                    <div className="flex gap-4 mt-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Principal: </span>
                        <span className="font-medium">{withdrawalInfo.totalPrincipalFormatted}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Rendimento: </span>
                        <span className="font-medium text-green-600">{withdrawalInfo.totalYieldFormatted}</span>
                      </div>
                    </div>
                    {withdrawalInfo.blockedBalance > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Bloqueado em resgates pendentes: {withdrawalInfo.blockedBalanceFormatted}
                      </p>
                    )}
                  </div>

                  {/* Opções de Resgate */}
                  <div className="space-y-2">
                    <Label>Quanto você deseja resgatar?</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setWithdrawalType("ALL")}
                        disabled={withdrawalInfo.availableBalance < minAmount}
                        className={`p-3 rounded-lg border text-center transition-colors ${
                          withdrawalType === "ALL"
                            ? "border-primary bg-primary/10"
                            : "border-border bg-muted hover:border-primary/50"
                        } ${withdrawalInfo.availableBalance < minAmount ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <p className="text-xs font-medium">Resgatar Tudo</p>
                        <p className="text-sm font-bold mt-1">{withdrawalInfo.availableBalanceFormatted}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setWithdrawalType("PRINCIPAL_ONLY")}
                        disabled={withdrawalInfo.totalPrincipal < minAmount}
                        className={`p-3 rounded-lg border text-center transition-colors ${
                          withdrawalType === "PRINCIPAL_ONLY"
                            ? "border-primary bg-primary/10"
                            : "border-border bg-muted hover:border-primary/50"
                        } ${withdrawalInfo.totalPrincipal < minAmount ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <p className="text-xs font-medium">Só Principal</p>
                        <p className="text-sm font-bold mt-1">{withdrawalInfo.totalPrincipalFormatted}</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setWithdrawalType("YIELD_ONLY")}
                        disabled={withdrawalInfo.totalYield < minAmount}
                        className={`p-3 rounded-lg border text-center transition-colors ${
                          withdrawalType === "YIELD_ONLY"
                            ? "border-primary bg-primary/10"
                            : "border-border bg-muted hover:border-primary/50"
                        } ${withdrawalInfo.totalYield < minAmount ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <p className="text-xs font-medium">Só Rendimento</p>
                        <p className="text-sm font-bold mt-1 text-green-600">{withdrawalInfo.totalYieldFormatted}</p>
                      </button>
                    </div>
                  </div>

                  {/* Valor Customizado */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setWithdrawalType("CUSTOM")}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          withdrawalType === "CUSTOM" ? "border-primary" : "border-muted-foreground"
                        }`}
                      >
                        {withdrawalType === "CUSTOM" && (
                          <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                        )}
                      </button>
                      <Label htmlFor="customAmount" className="cursor-pointer" onClick={() => setWithdrawalType("CUSTOM")}>
                        Ou digite um valor específico:
                      </Label>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                        R$
                      </span>
                      <Input
                        id="customAmount"
                        placeholder="0,00"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        onFocus={() => setWithdrawalType("CUSTOM")}
                        className="pl-10"
                        inputMode="numeric"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Mínimo: {withdrawalInfo.minWithdrawalAmountFormatted}
                    </p>
                    {withdrawalType === "CUSTOM" && (
                      <>
                        {parseCurrencyInput(customAmount) * 100 > withdrawalInfo.availableBalance && (
                          <p className="text-xs text-destructive">Valor excede o saldo disponível</p>
                        )}
                        {parseCurrencyInput(customAmount) > 0 && parseCurrencyInput(customAmount) * 100 < minAmount && (
                          <p className="text-xs text-destructive">
                            Valor mínimo: {withdrawalInfo.minWithdrawalAmountFormatted}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : null}

              {/* Conta Bancária */}
              <div className="space-y-2">
                <Label>Conta de Destino</Label>

                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : bankAccounts.length === 0 ? (
                  <div className="text-center py-4 bg-muted rounded-lg">
                    <Landmark className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mb-2">
                      Nenhuma conta cadastrada
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Cadastre uma conta em Contas Bancárias
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {bankAccounts.map((account) => (
                      <button
                        key={account.id}
                        onClick={() => setSelectedAccountId(account.id)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedAccountId === account.id
                            ? "border-primary bg-primary/5"
                            : "border-border bg-muted hover:border-primary/50"
                        }`}
                      >
                        <p className="font-medium text-sm">
                          {account.bankName} - Ag. {account.agency}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          CC: {account.accountNumber}
                          {account.accountDigit && `-${account.accountDigit}`}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancelar
                </Button>
                <Button
                  onClick={() => setStep("confirm")}
                  disabled={!isValidAmount || !selectedAccountId || bankAccounts.length === 0}
                >
                  Continuar
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {/* Confirm Step */}
        {step === "confirm" && selectedAccount && withdrawalInfo && (
          <>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Confirmar Resgate</CardTitle>
              <Button variant="ghost" size="sm" onClick={handleClose}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Revise os dados antes de confirmar.
              </p>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {/* Valor Total */}
              <div className="bg-muted rounded-lg p-4 text-center">
                <p className="text-sm text-muted-foreground">Valor do Resgate</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(withdrawalAmount / 100)}
                </p>
              </div>

              {/* Divisão Principal/Rendimento */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Principal</span>
                  <span className="font-medium">{formatCurrency(withdrawalSplit.principal / 100)}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-muted-foreground">Rendimento</span>
                  <span className="font-medium text-green-600">{formatCurrency(withdrawalSplit.yield / 100)}</span>
                </div>
              </div>

              {/* Conta Destino */}
              <div className="bg-muted rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">Conta de Destino</p>
                <p className="font-medium">
                  {selectedAccount.bankName}
                </p>
                <p className="text-sm text-muted-foreground">
                  Ag. {selectedAccount.agency} | CC{" "}
                  {selectedAccount.accountNumber}
                  {selectedAccount.accountDigit && `-${selectedAccount.accountDigit}`}
                </p>
              </div>

              {/* Aviso */}
              <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-700 dark:text-blue-300">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p className="text-xs">
                  Após confirmar, o valor ficará bloqueado até o processamento.
                  Você pode cancelar enquanto estiver pendente.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setStep("form")}>
                  Voltar
                </Button>
                <Button onClick={handleConfirm} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Confirmar Resgate
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {/* Success Step */}
        {step === "success" && (
          <CardContent className="py-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  Solicitação Enviada!
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Seu resgate de {formatCurrency(withdrawalAmount / 100)} foi solicitado e
                  está aguardando processamento.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Você receberá um email quando o resgate for concluído.
                </p>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <Button onClick={handleClose}>Fechar</Button>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
